import { useState } from "react";
import axios from "axios";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 text-sm text-[#0F2645] placeholder:text-[#93A0BC] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] focus:border-[#3A5CC8] transition-colors duration-200";

const initialForm = { name: "", email: "", subject: "", message: "" };

export const ContactSection = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useLanguage();

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const canSubmit = form.name.trim() && form.email.trim() && form.subject.trim() && form.message.trim();

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch (e) {
      toast.error(t("Une erreur est survenue. Réessayez.") + " info@devis-to.be");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 sm:py-32 border-t border-[#E2E7F7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <div>
          <p className="text-sm font-bold tracking-[0.1em] uppercase text-[#3A5CC8]">{t("Contact")}</p>
          <h2 data-testid="contact-title" className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-[#0F2645]">
            {t("Une question ? Parlons-en.")}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#5C6A87] max-w-md">
            {t("Une question sur Devisto Studio, une démonstration pour votre équipe, un besoin particulier pour votre métier ? Écrivez-nous, nous répondons rapidement.")}
          </p>
          <a
            href="mailto:info@devis-to.be"
            data-testid="contact-email-link"
            className="mt-8 inline-flex items-center gap-3 text-base font-medium text-slate-800 hover:text-[#0F2645] transition-colors duration-200"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#F6F8FD] border border-[#E2E7F7]">
              <Mail className="w-5 h-5 text-[#3A5CC8]" />
            </span>
            info@devis-to.be
          </a>
        </div>

        <div className="rounded-2xl border border-[#E2E7F7] bg-white shadow-sm p-6 sm:p-8">
          {done ? (
            <div data-testid="contact-success" className="text-center py-12 animate-fade-up">
              <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
              <h3 className="mt-5 font-heading text-2xl font-semibold text-[#0F2645]">{t("Message envoyé !")}</h3>
              <p className="mt-3 text-sm text-[#5C6A87]">{t("Nous revenons vers vous au plus vite.")}</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-[#0F2645] mb-2">{t("Nom")}</label>
                  <input id="contact-name" data-testid="contact-name" className={inputCls} placeholder={t("Votre nom")} value={form.name} onChange={(e) => set("name", e.target.value)} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-[#0F2645] mb-2">{t("Email")}</label>
                  <input id="contact-email" type="email" data-testid="contact-email" className={inputCls} placeholder="vous@exemple.be" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-[#0F2645] mb-2">{t("Sujet")}</label>
                <input id="contact-subject" data-testid="contact-subject" className={inputCls} placeholder={t("Ex. : Démonstration pour mon entreprise")} value={form.subject} onChange={(e) => set("subject", e.target.value)} />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-[#0F2645] mb-2">{t("Message")}</label>
                <textarea id="contact-message" data-testid="contact-message" rows={5} className={inputCls} placeholder={t("Votre message…")} value={form.message} onChange={(e) => set("message", e.target.value)} />
              </div>
              <button
                type="button"
                data-testid="contact-submit-btn"
                disabled={!canSubmit || submitting}
                onClick={submit}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5CC8] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-blue-600/25 hover:bg-[#3A5CC8] disabled:opacity-40 disabled:pointer-events-none transition-[background-color,transform] duration-200 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
              >
                {submitting ? t("Envoi en cours…") : t("Envoyer le message")} <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
